import { useEffect, useState } from "react";
import { FileText, Download, Trash2, Plus, Scale, ArrowLeft } from "lucide-react";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import api from "../lib/api";
import { toast } from "sonner";

export default function Documents() {
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [mode, setMode] = useState("list"); // list | generate | view

  // form state
  const [docType, setDocType] = useState("");
  const [formData, setFormData] = useState({
    expediteur_nom: "",
    expediteur_adresse: "",
    destinataire_nom: "",
    destinataire_adresse: "",
    objet: "",
    faits: "",
    demande: "",
  });
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [t, d, c] = await Promise.all([
        api.get("/document-templates"),
        api.get("/documents"),
        api.get("/chat/conversations"),
      ]);
      setTemplates(t.data);
      setDocuments(d.data);
      setConversations(c.data);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!docType) {
      toast.error("Choisissez un type de document");
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post("/documents/generate", {
        document_type: docType,
        context: formData,
      });
      toast.success("Document généré");
      setSelectedDoc(res.data);
      setMode("view");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de génération");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Document supprimé");
      if (selectedDoc?.document_id === id) {
        setSelectedDoc(null);
        setMode("list");
      }
      load();
    } catch {
      toast.error("Suppression échouée");
    }
  };

  const handleDownload = () => {
    if (!selectedDoc) return;
    const blob = new Blob([selectedDoc.content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedDoc.document_type}_${selectedDoc.document_id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar conversations={conversations} onNewChat={() => (window.location.href = "/dashboard")} />
      <main className="flex-1 flex flex-col min-w-0" data-testid="documents-main">
        <header className="border-b border-slate-200 bg-white/80 backdrop-blur-xl sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Documents</p>
              <h1 className="font-serif-display text-xl font-medium tracking-tight text-slate-900">
                Générateur juridique
              </h1>
            </div>
            {mode === "list" && (
              <Button
                onClick={() => { setMode("generate"); setSelectedDoc(null); }}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                data-testid="new-document-button"
              >
                <Plus className="h-4 w-4 mr-2" /> Nouveau document
              </Button>
            )}
            {mode !== "list" && (
              <Button
                onClick={() => { setMode("list"); setSelectedDoc(null); }}
                variant="ghost"
                className="rounded-sm"
                data-testid="back-to-list-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Retour
              </Button>
            )}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto custom-scroll p-6">
          <div className="max-w-5xl mx-auto">
            {mode === "list" && (
              <>
                {documents.length === 0 ? (
                  <div className="text-center py-20" data-testid="documents-empty">
                    <FileText className="h-10 w-10 mx-auto text-slate-300 mb-4" />
                    <h3 className="font-serif-display text-2xl font-medium mb-2">Aucun document</h3>
                    <p className="text-sm text-slate-500 mb-6">
                      Générez votre premier document juridique en quelques clics.
                    </p>
                    <Button
                      onClick={() => setMode("generate")}
                      className="rounded-sm bg-slate-900 hover:bg-slate-800"
                      data-testid="empty-new-document-button"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Créer un document
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {documents.map((d) => (
                      <div
                        key={d.document_id}
                        className="group bg-white border border-slate-200 rounded-sm p-6 hover:border-slate-900 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => { setSelectedDoc(d); setMode("view"); }}
                        data-testid={`document-card-${d.document_id}`}
                      >
                        <FileText className="h-4 w-4 text-[#B89C65] mb-4" />
                        <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">{d.label}</p>
                        <p className="text-sm font-medium text-slate-900 line-clamp-2 mb-4">
                          {d.context?.objet || "Sans objet"}
                        </p>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{new Date(d.created_at).toLocaleDateString("fr-BE")}</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(d.document_id); }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500"
                            data-testid={`delete-document-${d.document_id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {mode === "generate" && (
              <div className="bg-white border border-slate-200 rounded-sm p-8" data-testid="generate-form">
                <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-2">Nouveau document</p>
                <h2 className="font-serif-display text-3xl font-medium mb-8">Créer un document juridique</h2>
                <form onSubmit={handleGenerate} className="space-y-6">
                  <div>
                    <Label className="text-xs uppercase tracking-widest mb-2 block">Type de document</Label>
                    <Select value={docType} onValueChange={setDocType}>
                      <SelectTrigger className="rounded-sm" data-testid="document-type-select">
                        <SelectValue placeholder="Choisir un type" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id} data-testid={`doc-type-${t.id}`}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {docType && (
                      <p className="text-xs text-slate-500 mt-2">
                        {templates.find((t) => t.id === docType)?.description}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-widest mb-2 block">Votre nom</Label>
                      <Input
                        required
                        value={formData.expediteur_nom}
                        onChange={(e) => setFormData({ ...formData, expediteur_nom: e.target.value })}
                        className="rounded-sm"
                        data-testid="input-expediteur-nom"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest mb-2 block">Votre adresse</Label>
                      <Input
                        value={formData.expediteur_adresse}
                        onChange={(e) => setFormData({ ...formData, expediteur_adresse: e.target.value })}
                        className="rounded-sm"
                        data-testid="input-expediteur-adresse"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest mb-2 block">Destinataire</Label>
                      <Input
                        required
                        value={formData.destinataire_nom}
                        onChange={(e) => setFormData({ ...formData, destinataire_nom: e.target.value })}
                        className="rounded-sm"
                        data-testid="input-destinataire-nom"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest mb-2 block">Adresse destinataire</Label>
                      <Input
                        value={formData.destinataire_adresse}
                        onChange={(e) => setFormData({ ...formData, destinataire_adresse: e.target.value })}
                        className="rounded-sm"
                        data-testid="input-destinataire-adresse"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-widest mb-2 block">Objet</Label>
                    <Input
                      required
                      value={formData.objet}
                      onChange={(e) => setFormData({ ...formData, objet: e.target.value })}
                      className="rounded-sm"
                      placeholder="Ex: Résiliation du bail situé au..."
                      data-testid="input-objet"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest mb-2 block">Exposé des faits</Label>
                    <Textarea
                      required
                      value={formData.faits}
                      onChange={(e) => setFormData({ ...formData, faits: e.target.value })}
                      rows={4}
                      className="rounded-sm"
                      placeholder="Décrivez la situation..."
                      data-testid="input-faits"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest mb-2 block">Votre demande</Label>
                    <Textarea
                      required
                      value={formData.demande}
                      onChange={(e) => setFormData({ ...formData, demande: e.target.value })}
                      rows={3}
                      className="rounded-sm"
                      placeholder="Ce que vous souhaitez obtenir..."
                      data-testid="input-demande"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={generating}
                    className="rounded-sm bg-slate-900 hover:bg-slate-800 h-11 px-8"
                    data-testid="generate-document-button"
                  >
                    {generating ? "Génération en cours..." : "Générer le document"}
                  </Button>
                </form>
              </div>
            )}

            {mode === "view" && selectedDoc && (
              <div className="bg-white border border-slate-200 rounded-sm" data-testid="document-view">
                <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#B89C65] mb-1">{selectedDoc.label}</p>
                    <h2 className="font-serif-display text-2xl font-medium">{selectedDoc.context?.objet}</h2>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleDownload}
                      variant="outline"
                      className="rounded-sm"
                      data-testid="download-document-button"
                    >
                      <Download className="h-4 w-4 mr-2" /> Télécharger
                    </Button>
                    <Button
                      onClick={() => handleDelete(selectedDoc.document_id)}
                      variant="outline"
                      className="rounded-sm text-red-600 hover:text-red-700"
                      data-testid="delete-viewed-document"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                    </Button>
                  </div>
                </div>
                <div className="p-8">
                  <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans text-slate-800" data-testid="document-content">
                    {selectedDoc.content}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
